/**
 * useSchoolRoster – one school's classes, teachers and students, live.
 *
 * Every Classroom Management tab reads from here, so the school is subscribed
 * to once and an edit made through the server shows up everywhere without a
 * refetch. The old page read classes once and never refreshed.
 *
 * The lookups (students per class, classes per teacher) are built once per
 * snapshot rather than by filtering the whole school inside each row.
 */

import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Class } from '../types/lms';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface RosterPerson {
  id: string;
  name: string;
  email: string;
  approvalStatus: ApprovalStatus | null;
  /** Epoch ms the account was created, when known. */
  createdAt: number | null;
  classIds: string[];
  /** Students: the class asked for at signup, until approved. */
  requestedClassId: string | null;
  subjects: string[];
  rejectionReason?: string | null;
}

export interface SchoolRoster {
  classes: Class[];
  teachers: RosterPerson[];
  students: RosterPerson[];
  classById: Map<string, Class>;
  teacherById: Map<string, RosterPerson>;
  /** Class ids each teacher teaches, from the classes (the source of truth). */
  classesByTeacher: Map<string, Class[]>;
  /** Classes whose class teacher is this teacher. */
  classTeacherOf: Map<string, Class[]>;
  loading: boolean;
  error: string | null;
}

const millis = (v: any): number | null => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

const toPerson = (id: string, d: any, classField: 'class_ids' | 'managed_class_ids'): RosterPerson => ({
  id,
  name: d.name || d.displayName || d.email || 'Unnamed',
  email: d.email || '',
  approvalStatus: d.approvalStatus ?? null,
  createdAt: millis(d.createdAt),
  classIds: Array.isArray(d[classField]) ? d[classField] : [],
  requestedClassId: d.requested_class_id ?? null,
  subjects: Array.isArray(d.subjectsTaught) ? d.subjectsTaught : [],
  rejectionReason: d.rejectionReason ?? null,
});

const byName = (a: { name?: string; class_name?: string }, b: { name?: string; class_name?: string }) =>
  String(a.name ?? a.class_name ?? '').localeCompare(String(b.name ?? b.class_name ?? ''), undefined, { numeric: true, sensitivity: 'base' });

export function useSchoolRoster(schoolId: string | null | undefined): SchoolRoster {
  const [classes, setClasses] = useState<Class[] | null>(null);
  const [teachers, setTeachers] = useState<RosterPerson[] | null>(null);
  const [students, setStudents] = useState<RosterPerson[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setClasses(null);
    setTeachers(null);
    setStudents(null);
    setError(null);
    if (!schoolId) return;

    const fail = (what: string) => (err: Error) => {
      console.error(`[useSchoolRoster] ${what} could not be loaded:`, err);
      setError(`The school's ${what} could not be loaded. You may not have access to this school.`);
    };
    const users = collection(db, 'users');
    const unsubs = [
      onSnapshot(
        query(collection(db, 'classes'), where('school_id', '==', schoolId)),
        (snap) =>
          setClasses(
            snap.docs
              .map((d) => ({ id: d.id, ...(d.data() as any) }) as Class)
              .filter((c) => c.archived !== true)
              .sort(byName)
          ),
        fail('classes')
      ),
      onSnapshot(
        query(users, where('role', '==', 'teacher'), where('school_id', '==', schoolId)),
        (snap) => setTeachers(snap.docs.map((d) => toPerson(d.id, d.data(), 'managed_class_ids')).sort(byName)),
        fail('teachers')
      ),
      onSnapshot(
        query(users, where('role', '==', 'student'), where('school_id', '==', schoolId)),
        (snap) => setStudents(snap.docs.map((d) => toPerson(d.id, d.data(), 'class_ids')).sort(byName)),
        fail('students')
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, [schoolId]);

  const derived = useMemo(() => {
    const list = classes ?? [];
    const classById = new Map(list.map((c) => [c.id, c]));
    const classesByTeacher = new Map<string, Class[]>();
    const classTeacherOf = new Map<string, Class[]>();
    list.forEach((c) => {
      (c.teacher_ids ?? []).forEach((t) => classesByTeacher.set(t, [...(classesByTeacher.get(t) ?? []), c]));
      if (c.class_teacher_id) classTeacherOf.set(c.class_teacher_id, [...(classTeacherOf.get(c.class_teacher_id) ?? []), c]);
    });
    return {
      classById,
      classesByTeacher,
      classTeacherOf,
      teacherById: new Map((teachers ?? []).map((t) => [t.id, t])),
    };
  }, [classes, teachers]);

  return {
    classes: classes ?? [],
    teachers: teachers ?? [],
    students: students ?? [],
    ...derived,
    loading: !error && Boolean(schoolId) && (classes === null || teachers === null || students === null),
    error,
  };
}
